import { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { List, Ref } from 'semantic-ui-react';
import './sortableDescriptorItem.css';

type SortableDescriptorItemPropsType = {
    id: string;
    dragDisabled: boolean;
    style: CSSProperties;
    className: string;
    onDoubleClick: (e: MouseEvent) => void;
    children: (dragHandle: ReactNode) => ReactNode;
};

/**
 * Sortable list item for a descriptor row. Provides the drag handle via a
 * render prop so it can be placed inside the row header, next to the
 * field options dropdown.
 */
function SortableDescriptorItem({
    id,
    dragDisabled,
    style,
    className,
    onDoubleClick,
    children,
}: SortableDescriptorItemPropsType) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        disabled: dragDisabled,
    });

    const dragHandle = dragDisabled ? null : (
        <button
            type="button"
            className="descriptorDragHandle"
            {...attributes}
            {...listeners}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <GripVertical size={16} />
        </button>
    );

    return (
        <Ref innerRef={setNodeRef}>
            <List.Item
                className={className + (isDragging ? ' descriptorItemDragging' : '')}
                /* Translate only (no scale): rows have variable heights, and the
                   scale part would stretch a row to the size of the slot it swaps with. */
                style={{ ...style, transform: CSS.Translate.toString(transform), transition }}
                onDoubleClick={onDoubleClick}
            >
                {children(dragHandle)}
            </List.Item>
        </Ref>
    );
}

export default SortableDescriptorItem;
